
import React from 'react';
import ModernPolicyLayout from '@/components/legal/ModernPolicyLayout';

const EULA = () => {
  const tableOfContents = [
    { id: 'acceptance', title: 'Acceptance of Terms', level: 1 },
    { id: 'eligibility', title: 'Eligibility & Age Requirements', level: 1 },
    { id: 'grant', title: 'Grant of License', level: 1 },
    { id: 'restrictions', title: 'License Restrictions', level: 1 },
    { id: 'user-conduct', title: 'User Conduct & Acceptable Use', level: 1 },
    { id: 'intellectual-property', title: 'Intellectual Property Rights', level: 1 },
    { id: 'user-content', title: 'User Content and Data', level: 1 },
    { id: 'ai-processing', title: 'AI & Automated Processing Consent', level: 1 },
    { id: 'data-processing', title: 'Data Processing & International Transfers', level: 1 },
    { id: 'payment', title: 'Payment & Financial Data', level: 1 },
    { id: 'updates', title: 'Software Updates', level: 1 },
    { id: 'beta-features', title: 'Beta Features', level: 1 },
    { id: 'third-party', title: 'Third-Party Services', level: 1 },
    { id: 'account-suspension', title: 'Account Suspension & Content Removal', level: 1 },
    { id: 'device-data', title: 'Device & Biometric Data', level: 1 },
    { id: 'termination', title: 'Termination', level: 1 },
    { id: 'warranties', title: 'Warranty Disclaimers', level: 1 },
    { id: 'liability', title: 'Limitation of Liability', level: 1 },
    { id: 'indemnification', title: 'Indemnification', level: 1 },
    { id: 'disputes', title: 'Dispute Resolution & Arbitration', level: 1 },
    { id: 'class-action-waiver', title: 'Class Action Waiver', level: 1 },
    { id: 'export', title: 'Export Compliance', level: 1 },
    { id: 'government', title: 'Government Users', level: 1 },
    { id: 'accessibility', title: 'Accessibility Statement', level: 1 },
    { id: 'modification', title: 'Modification of Terms', level: 1 },
    { id: 'entire-agreement', title: 'Entire Agreement', level: 1 },
    { id: 'severability', title: 'Severability', level: 1 },
    { id: 'assignment', title: 'Assignment', level: 1 },
    { id: 'force-majeure', title: 'Force Majeure', level: 1 },
    { id: 'contact', title: 'Contact Information', level: 1 }
  ];

  return (
    <ModernPolicyLayout 
      title="End User License Agreement (EULA)" 
      estimatedReadTime="25 min read"
      lastUpdated="Last updated: March 9, 2026"
      tableOfContents={tableOfContents}
    >
      <div className="space-y-8">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
          <p className="text-sm">
            <strong>IMPORTANT — READ CAREFULLY:</strong> This End User License Agreement ("EULA" or "Agreement") is a legally binding contract between you ("User," "you," or "your") and EquiForge™, Inc. ("EquiForge," "Company," "we," "us," or "our"). 
            By creating an account, installing, accessing, or using the EquiForge™ Homebuyer Optimizer application or any related services (collectively, the "Service"), you agree to be bound by all terms of this EULA, our Terms of Service, Privacy Policy, Cookie Policy, and all applicable laws and regulations. 
            If you do not agree to these terms, you must immediately cease all use of the Service and delete your account.
          </p>
          <p className="text-sm mt-3 font-semibold">
            THIS AGREEMENT CONTAINS AN ARBITRATION CLAUSE AND CLASS ACTION WAIVER IN SECTIONS 20 AND 21. PLEASE READ THESE SECTIONS CAREFULLY AS THEY AFFECT YOUR LEGAL RIGHTS.
          </p>
        </div>

        <section id="acceptance">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By clicking "I Accept," creating an account, or otherwise accessing or using the Service, you represent and warrant that:
          </p>
          <ul>
            <li>You have read, understood, and agree to be bound by this EULA in its entirety</li>
            <li>You have the legal capacity and authority to enter into this Agreement</li>
            <li>If you are accepting on behalf of an organization, you have the authority to bind that organization to these terms</li>
            <li>You consent to the collection, use, and processing of your data as described in our Privacy Policy</li>
            <li>You acknowledge that this EULA, together with our Terms of Service, Privacy Policy, and Cookie Policy, constitutes the complete agreement governing your use of the Service</li>
          </ul>
          <p>
            Your continued use of the Service following the posting of changes to this EULA constitutes your acceptance of such changes. We will notify you of material changes at least 30 days before they take effect.
          </p>
        </section>

        <section id="eligibility">
          <h2>2. Eligibility & Age Requirements</h2>
          <p>
            <strong>Minimum Age:</strong> You must be at least 18 years of age (or the age of legal majority in your jurisdiction) to use the Service. The Service is not directed to, and should not be used by, individuals under 18 years of age. If we learn that we have collected personal information from a child under 18 without parental consent, we will take steps to delete such information promptly.
          </p>
          <p>
            <strong>COPPA Compliance:</strong> In compliance with the Children's Online Privacy Protection Act (COPPA), we do not knowingly collect, maintain, or use personal information from children under 13 years of age. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at privacy@equiforge.com.
          </p>
          <p>
            <strong>Capacity:</strong> By using the Service, you represent that you are not barred from receiving services under the laws of your applicable jurisdiction. You are responsible for ensuring that your use complies with all local laws, including any financial regulations applicable to your jurisdiction.
          </p>
        </section>

        <section id="grant">
          <h2>3. Grant of License</h2>
          <p>
            Subject to your compliance with this EULA, EquiForge™ grants you a limited, non-exclusive, 
            non-transferable, non-sublicensable, revocable license to:
          </p>
          <ul>
            <li>Install and use the EquiForge™ Homebuyer Optimizer application on devices you own or control</li>
            <li>Access and use the application's features and functionality for personal, non-commercial purposes unless you hold a valid commercial license</li>
            <li>Receive automatic updates and new versions of the application, subject to additional terms</li>
            <li>Generate, view, and export reports and calculations for your personal or authorized business use</li>
          </ul>
          <p>
            This license is conditional upon your continued compliance with all terms herein. This license does not grant you ownership of the application or any proprietary rights. All rights not expressly granted are reserved by EquiForge™.
          </p>
        </section>

        <section id="restrictions">
          <h2>4. License Restrictions</h2>
          <p>You agree that you will NOT:</p>
          <ul>
            <li>Copy, modify, adapt, translate, or create derivative works of the application or any part thereof</li>
            <li>Reverse engineer, decompile, disassemble, or attempt to discover the source code, object code, or underlying structure, algorithms, or APIs</li>
            <li>Remove, alter, or obscure any proprietary notices, labels, marks, or watermarks</li>
            <li>Rent, lease, lend, sell, redistribute, sublicense, or otherwise transfer the application or any rights therein</li>
            <li>Use the application for any unlawful purpose or in violation of any applicable law or regulation</li>
            <li>Interfere with or disrupt the integrity, performance, or security of the application or its infrastructure</li>
            <li>Access the application through automated means (bots, scrapers, crawlers, spiders) without explicit written authorization</li>
            <li>Use the application to transmit malware, viruses, worms, Trojan horses, or any other harmful or malicious code</li>
            <li>Attempt to gain unauthorized access to any systems, networks, servers, or databases connected to the Service</li>
            <li>Use the Service to engage in competitive analysis, benchmarking, or to develop a competing product</li>
            <li>Circumvent, disable, or interfere with any security, authentication, or access control features</li>
            <li>Harvest, scrape, or collect any user data, personal information, or proprietary data from the Service</li>
            <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
            <li>Use the Service to send unsolicited communications, spam, or promotional materials</li>
          </ul>
        </section>

        <section id="user-conduct">
          <h2>5. User Conduct & Acceptable Use</h2>
          <p>
            You are solely responsible for your conduct and any content, data, or information you submit, post, or display on or through the Service. You agree not to:
          </p>
          <ul>
            <li>Upload, post, or transmit content that is unlawful, defamatory, obscene, threatening, harassing, discriminatory, or otherwise objectionable</li>
            <li>Engage in any activity that could damage, disable, overburden, or impair the Service or interfere with any other party's use</li>
            <li>Use the Service to stalk, intimidate, or harass any person</li>
            <li>Submit false, inaccurate, or misleading information, including fraudulent financial data</li>
            <li>Violate any applicable local, state, national, or international law, including securities regulations, real estate regulations, or fair lending laws</li>
            <li>Use the Service in any manner that infringes upon the intellectual property or privacy rights of any third party</li>
            <li>Attempt to probe, scan, or test the vulnerability of the Service or any related system or network</li>
            <li>Share your account credentials with any third party or allow any unauthorized person to access the Service using your account</li>
            <li>Use the Service in any capacity that requires professional licensing (e.g., acting as a financial advisor, mortgage broker, or real estate agent) without holding the appropriate license in your jurisdiction</li>
          </ul>
          <p>
            <strong>Enforcement:</strong> We reserve the right to investigate and take appropriate legal action against anyone who, in our sole discretion, violates this provision, including removing offending content, suspending or terminating accounts, and reporting violations to law enforcement authorities.
          </p>
        </section>

        <section id="intellectual-property">
          <h2>6. Intellectual Property Rights</h2>
          <p>
            The EquiForge™ Homebuyer Optimizer application, including all content, features, functionality, 
            software code, design elements, trademarks, service marks, logos, algorithms, AI models, databases, and proprietary methodologies, is owned by EquiForge™ 
            or its licensors and is protected by United States and international copyright, trademark, 
            patent, trade secret, and other intellectual property laws.
          </p>
          <p>
            <strong>Trademarks:</strong> "EquiForge™," "Homebuyer Optimizer," "CLARA," "SmartBuy Analyzer," "HomeQuest," the EquiForge logo, and all related names, logos, 
            product and service names, designs, and slogans are trademarks of EquiForge™ or its affiliates. 
            You must not use such marks without prior written permission.
          </p>
          <p>
            <strong>Copyright:</strong> All content within the application, including but not limited to text, graphics, user interfaces, visual interfaces, photographs, sounds, artwork, computer code, algorithms, and the arrangement thereof, is protected by copyright. 
            Unauthorized copying, distribution, modification, public display, or creation of derivative works is strictly prohibited and may subject you to civil and criminal penalties.
          </p>
          <p>
            <strong>Patents:</strong> The Service may be protected by one or more patents or pending patent applications. Nothing in this EULA grants you any rights to our patents.
          </p>
          <p>
            <strong>Feedback:</strong> If you provide suggestions, ideas, or feedback regarding the Service ("Feedback"), you grant EquiForge™ an irrevocable, perpetual, worldwide, royalty-free license to use, modify, distribute, and incorporate such Feedback without any obligation or compensation to you.
          </p>
        </section>

        <section id="user-content">
          <h2>7. User Content and Data</h2>
          <p>
            <strong>Your Data:</strong> You retain all rights to any data, calculations, financial information, or other content 
            you input into the application ("User Content"). EquiForge™ does not claim ownership of your personal or 
            financial information.
          </p>
          <p>
            <strong>License Grant:</strong> By submitting User Content to the Service, you grant EquiForge™ a non-exclusive, worldwide, royalty-free, sublicensable license to use, reproduce, modify, adapt, and process your User Content solely for the purposes of: (a) providing, maintaining, and improving the Service; (b) developing new features and services; (c) generating anonymized, aggregated analytics and insights; and (d) complying with legal obligations.
          </p>
          <p>
            <strong>Data Storage:</strong> Financial calculations and personal preferences may be stored 
            on secure cloud infrastructure with encryption at rest and in transit. You are responsible for maintaining backups 
            of important data. While we employ industry-standard security measures, no system is completely impervious to attack.
          </p>
          <p>
            <strong>Anonymized Data:</strong> We may use anonymized, de-identified, and aggregated data derived from your use of the Service for analytics, research, benchmarking, and product improvement. Such data will not identify you personally. We will never sell your personally identifiable financial information to third parties.
          </p>
          <p>
            <strong>Data Deletion:</strong> You may request deletion of your data at any time by contacting 
            privacy@equiforge.com or using the account deletion feature in Settings. We will comply with applicable data protection laws, including GDPR and CCPA. Some data may be retained as required by law or for legitimate business purposes as outlined in our Privacy Policy.
          </p>
        </section>

        <section id="ai-processing">
          <h2>8. AI & Automated Processing Consent (ISO 42001 Compliance)</h2>
          <p>
            The Service incorporates artificial intelligence ("AI") and machine learning technologies, including but not limited to:
          </p>
          <ul>
            <li><strong>CLARA:</strong> Our AI-powered conversational homebuying assistant that uses natural language processing to guide you through the homebuying process</li>
            <li><strong>Deal Analyzer:</strong> AI-driven property analysis that evaluates investment potential, market trends, and financial projections</li>
            <li><strong>SmartBuy Analyzer:</strong> Automated affordability and buying power calculations</li>
            <li><strong>Market Intelligence:</strong> AI-generated market insights, trend analysis, and property recommendations</li>
            <li><strong>Document Analysis:</strong> Automated processing and extraction of information from uploaded documents</li>
          </ul>
          <p>
            <strong>Consent:</strong> By using the Service, you expressly consent to the processing of your data by AI and automated systems for the purposes described above. You acknowledge that:
          </p>
          <ul>
            <li>AI outputs are generated algorithmically and may contain errors, biases, or inaccuracies</li>
            <li>AI-generated content does not constitute professional financial, legal, or real estate advice</li>
            <li>You should independently verify all AI-generated insights before making financial decisions</li>
            <li>AI models may be updated, retrained, or replaced without notice, which may affect outputs</li>
            <li>Your data may be used to improve AI models, but only in anonymized, aggregated form</li>
          </ul>
          <p>
            <strong>AI Governance:</strong> In accordance with ISO 42001 (AI Management System), EquiForge™ maintains an AI governance framework that includes: model transparency documentation, bias monitoring and mitigation, regular model audits, human oversight of critical AI decisions, and incident response procedures for AI-related issues. You may request information about how AI decisions affect you by contacting ai-governance@equiforge.com.
          </p>
          <p>
            <strong>Right to Human Review:</strong> Where AI-driven decisions significantly affect your access to or use of the Service, you have the right to request human review of such decisions by contacting support@equiforge.com.
          </p>
        </section>

        <section id="data-processing">
          <h2>9. Data Processing & International Transfers (GDPR/CCPA Compliance)</h2>
          <p>
            <strong>Data Controller:</strong> EquiForge™, Inc. acts as the data controller for personal data processed through the Service. Our data processing activities are described in detail in our Privacy Policy.
          </p>
          <p>
            <strong>Legal Basis for Processing (GDPR Article 6):</strong> We process personal data on the following legal bases:
          </p>
          <ul>
            <li><strong>Contract Performance:</strong> Processing necessary to provide the Service you requested</li>
            <li><strong>Legitimate Interests:</strong> Fraud prevention, security, service improvement, and analytics</li>
            <li><strong>Consent:</strong> Marketing communications, AI processing, and optional features</li>
            <li><strong>Legal Obligation:</strong> Tax reporting, regulatory compliance, and law enforcement requests</li>
          </ul>
          <p>
            <strong>International Data Transfers:</strong> Your data may be transferred to and processed in countries outside your jurisdiction, including the United States. Where such transfers occur, we ensure appropriate safeguards are in place in accordance with GDPR Articles 46 and 49, including Standard Contractual Clauses (SCCs) approved by the European Commission.
          </p>
          <p>
            <strong>Data Subject Rights (GDPR):</strong> If you are located in the European Economic Area (EEA), you have the right to: access, rectify, erase, restrict, port, and object to the processing of your personal data. You also have the right to withdraw consent at any time and to lodge a complaint with a supervisory authority.
          </p>
          <p>
            <strong>California Residents (CCPA/CPRA):</strong> California residents have additional rights under the California Consumer Privacy Act, including the right to know what personal information is collected, the right to delete personal information, the right to opt-out of the sale of personal information, and the right to non-discrimination for exercising privacy rights. We do not sell personal information as defined by the CCPA.
          </p>
          <p>
            <strong>U.S. State Privacy Laws:</strong> Residents of states with comprehensive privacy laws (Virginia, Colorado, Connecticut, Utah, Texas, Oregon, Montana, and others) may have additional rights. Please refer to our Privacy Policy for details.
          </p>
        </section>

        <section id="payment">
          <h2>10. Payment Terms & Financial Data Security (PCI-DSS Compliance)</h2>
          <p>
            <strong>Payment Processing:</strong> All payment processing is handled by Stripe, Inc., a PCI-DSS Level 1 certified payment processor. EquiForge™ does NOT store, process, or have access to your full credit card numbers, CVV codes, or other payment card data. All payment data is transmitted directly to Stripe via their secure, PCI-compliant infrastructure.
          </p>
          <p>
            <strong>Free Tier:</strong> The application offers a free tier with limited functionality. 
            Free tier users may experience reduced features, slower performance, or usage limits.
          </p>
          <p>
            <strong>Paid Subscriptions:</strong> Premium features require a paid subscription. By subscribing, you agree to:
          </p>
          <ul>
            <li>Pay all fees and charges associated with your subscription tier</li>
            <li>Provide accurate, current, and complete payment information</li>
            <li>Authorize automatic recurring billing according to your subscription plan</li>
            <li>Receive invoices and billing notices via email</li>
            <li>Update your payment information promptly if it changes</li>
          </ul>
          <p>
            <strong>Pricing Changes:</strong> EquiForge™ reserves the right to modify subscription prices 
            with 30 days advance notice via email. Price changes will not apply to current billing periods. Continued use after price changes take effect constitutes acceptance.
          </p>
          <p>
            <strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings or by contacting support@equiforge.com. Cancellations take effect at the end of the current billing period. No prorated refunds for partial periods unless required by applicable law.
          </p>
          <p>
            <strong>Refund Policy:</strong> Refunds may be provided at EquiForge™'s discretion for 
            billing errors, service failures, or other circumstances. Refund requests must be submitted within 30 days of the charge. Contact billing@equiforge.com for refund requests.
          </p>
          <p>
            <strong>Taxes:</strong> You are responsible for any applicable sales tax, VAT, GST, or other taxes in your jurisdiction. Prices displayed may not include taxes unless expressly stated.
          </p>
        </section>

        <section id="updates">
          <h2>11. Software Updates and Maintenance</h2>
          <p>
            EquiForge™ may automatically download and install updates, upgrades, patches, and bug fixes 
            to improve the application's performance, security, and functionality. These updates may:
          </p>
          <ul>
            <li>Add new features or enhance existing functionality</li>
            <li>Fix bugs, security vulnerabilities, or compatibility issues</li>
            <li>Modify or remove features that are no longer supported</li>
            <li>Change the user interface or user experience</li>
            <li>Update AI models or algorithms used within the Service</li>
          </ul>
          <p>
            You agree that EquiForge™ has no obligation to provide updates and may discontinue support 
            for older versions. Critical security updates may be installed automatically without prior notice.
          </p>
        </section>

        <section id="beta-features">
          <h2>12. Beta Features and Preview Functionality</h2>
          <p>
            The application may include beta, preview, early access, or experimental features that are not yet fully 
            tested or validated. These features:
          </p>
          <ul>
            <li>Are provided "AS IS" and "AS AVAILABLE" without warranties of any kind</li>
            <li>May contain bugs, errors, security vulnerabilities, or incomplete functionality</li>
            <li>May be modified, suspended, or removed at any time without notice</li>
            <li>Should NOT be relied upon for critical financial or real estate decisions</li>
            <li>May have different privacy or data handling characteristics than production features</li>
          </ul>
          <p>
            <strong>Beta User Responsibilities:</strong> If you participate in beta testing, you agree to 
            provide feedback and report bugs. You understand that beta features may result in data loss, 
            unexpected behavior, or service disruptions. EquiForge™ shall not be liable for any damages arising from your use of beta features.
          </p>
        </section>

        <section id="third-party">
          <h2>13. Third-Party Services and Integrations</h2>
          <p>
            The application integrates with or provides access to third-party services, including but not limited to:
          </p>
          <ul>
            <li>Authentication providers (Supabase Auth)</li>
            <li>Payment processors (Stripe — PCI-DSS Level 1 certified)</li>
            <li>AI/ML providers (OpenAI)</li>
            <li>Data providers (mortgage rate APIs, property databases, market data services)</li>
            <li>Analytics and error monitoring services (Sentry)</li>
            <li>Email delivery services</li>
            <li>Cloud infrastructure providers</li>
          </ul>
          <p>
            Your use of third-party services is subject to their respective terms of service and privacy 
            policies. EquiForge™ is not responsible for the availability, accuracy, content, privacy practices, or security of 
            third-party services. We are not liable for any damage or loss arising from your use of third-party services.
          </p>
        </section>

        <section id="account-suspension">
          <h2>14. Account Suspension & Content Removal</h2>
          <p>
            <strong>Suspension:</strong> EquiForge™ reserves the right to suspend your account, temporarily or permanently, with or without notice, if we reasonably believe that:
          </p>
          <ul>
            <li>You have violated any provision of this EULA, the Terms of Service, or any applicable law</li>
            <li>Your account has been compromised or shows signs of unauthorized access</li>
            <li>Your use of the Service poses a security risk to the Service, other users, or third parties</li>
            <li>You have engaged in fraudulent, abusive, or deceptive behavior</li>
            <li>Continued provision of the Service to you is no longer commercially viable</li>
            <li>We are required to do so by law, regulation, or court order</li>
          </ul>
          <p>
            <strong>Content Removal:</strong> We reserve the right to remove any User Content that violates this EULA, is objectionable, or that we determine in our sole discretion is harmful to the Service, its users, or EquiForge™'s reputation.
          </p>
          <p>
            <strong>Appeals:</strong> If your account is suspended or content is removed, you may appeal by contacting appeals@equiforge.com within 30 days. We will review your appeal and provide a response within 14 business days.
          </p>
        </section>

        <section id="device-data">
          <h2>15. Device, Technical & Biometric Data</h2>
          <p>
            The Service may collect technical information about your device and usage, including:
          </p>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system, browser type and version, screen resolution, and device identifiers</li>
            <li><strong>Usage Data:</strong> Pages visited, features used, session duration, click patterns, scroll depth, and interaction metrics</li>
            <li><strong>Network Information:</strong> IP address, approximate geographic location (city/region level), ISP information, and connection type</li>
            <li><strong>Session Recordings:</strong> Anonymized session replays for UX improvement (no keystrokes or form input data is captured in recordings)</li>
            <li><strong>Performance Data:</strong> Error logs, crash reports, API response times, and resource usage metrics</li>
          </ul>
          <p>
            <strong>Device Fingerprinting:</strong> We may use device fingerprinting techniques for security purposes, including fraud detection, session validation, and anomaly detection. This data is used to protect your account and is not shared with third parties for advertising purposes.
          </p>
          <p>
            <strong>Biometric Data:</strong> The Service does NOT collect biometric data such as fingerprints, facial recognition data, voiceprints, or iris scans. If this changes in the future, we will obtain your explicit opt-in consent before collecting any biometric data.
          </p>
        </section>

        <section id="termination">
          <h2>16. Termination</h2>
          <p>
            <strong>Termination by You:</strong> You may terminate this EULA at any time by deleting your account through Settings or by contacting support@equiforge.com. Upon termination, you must immediately cease all use of the Service.
          </p>
          <p>
            <strong>Termination by EquiForge™:</strong> We may suspend or terminate your access to the 
            application immediately, with or without notice, for any reason including if you:
          </p>
          <ul>
            <li>Violate any terms of this EULA or our Terms of Service</li>
            <li>Engage in fraudulent, abusive, or illegal activities</li>
            <li>Fail to pay subscription fees when due</li>
            <li>Pose a security risk to the application, its infrastructure, or other users</li>
            <li>Are the subject of a legal or regulatory action that prohibits continued use</li>
          </ul>
          <p>
            <strong>Effect of Termination:</strong> Upon termination: (a) all licenses granted herein immediately terminate; (b) you must cease all use of the Service; (c) you may request a copy of your data within 30 days of termination; (d) after 30 days, we may delete your data except as required by law. Provisions regarding intellectual property, disclaimers, limitations of liability, indemnification, and dispute resolution survive termination.
          </p>
        </section>

        <section id="warranties">
          <h2>17. Warranty Disclaimers</h2>
          <p className="font-semibold uppercase text-xs tracking-wide">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
            FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE 
            OF DEALING OR USAGE OF TRADE.
          </p>
          <p>
            EquiForge™ expressly disclaims all warranties including but not limited to:
          </p>
          <ul>
            <li>That the Service will be uninterrupted, timely, secure, error-free, or free of viruses or other harmful components</li>
            <li>That the results obtained from the Service will be accurate, reliable, or complete</li>
            <li>That any errors or defects in the Service will be corrected</li>
            <li>That the Service will meet your specific requirements or expectations</li>
            <li>The accuracy of any AI-generated content, predictions, or recommendations</li>
          </ul>
          <p>
            <strong>Financial Calculations:</strong> ALL mortgage calculations, affordability estimates, 
            investment projections, market analyses, and financial metrics are for INFORMATIONAL PURPOSES ONLY and do not constitute 
            professional financial, investment, legal, tax, or real estate advice. Actual loan terms, property values, and financial outcomes may vary significantly. Always consult qualified professionals before making financial decisions.
          </p>
        </section>

        <section id="liability">
          <h2>18. Limitation of Liability</h2>
          <p className="font-semibold uppercase text-xs tracking-wide">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL EQUIFORGE™, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, 
            AFFILIATES, SUCCESSORS, OR ASSIGNS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, 
            REGARDLESS OF THE CAUSE OF ACTION OR THE THEORY OF LIABILITY, EVEN IF EQUIFORGE™ HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            This limitation applies to damages for:
          </p>
          <ul>
            <li>Loss of profits, revenue, goodwill, data, or business opportunities</li>
            <li>Inaccurate calculations, AI predictions, or financial decisions based on Service data or outputs</li>
            <li>Service interruptions, downtime, data loss, or security breaches</li>
            <li>Unauthorized access to or alteration of your data or transmissions</li>
            <li>Personal injury or property damage arising from use of the Service</li>
            <li>Any conduct or content of any third party on the Service</li>
            <li>Any other matter relating to the Service</li>
          </ul>
          <p>
            <strong>Maximum Liability:</strong> In no event shall EquiForge™'s total aggregate liability to you for all claims arising out of or relating to this EULA or the Service exceed 
            the greater of: (a) the total amount you paid to EquiForge™ in the twelve (12) months preceding the event giving rise to the claim, or (b) one hundred U.S. dollars ($100.00 USD).
          </p>
          <p>
            Some jurisdictions do not allow the exclusion or limitation of liability for certain types of damages, so some of the above limitations may not apply to you. In such jurisdictions, EquiForge™'s liability is limited to the maximum extent permitted by law.
          </p>
        </section>

        <section id="indemnification">
          <h2>19. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless EquiForge™, its parent companies, subsidiaries, affiliates, officers, directors, 
            employees, agents, licensors, suppliers, and any third-party information providers from and against all claims, liabilities, damages, losses, costs, 
            and expenses (including reasonable attorneys' fees and court costs) arising out of or related to:
          </p>
          <ul>
            <li>Your violation of this EULA, our Terms of Service, or any applicable law or regulation</li>
            <li>Your use or misuse of the Service</li>
            <li>Financial or investment decisions made based on Service data, calculations, or AI outputs</li>
            <li>Your User Content or any content you submit through the Service</li>
            <li>Your infringement of any intellectual property, privacy, or other rights of any third party</li>
            <li>Any unauthorized access to or use of the Service through your account</li>
            <li>Any claim by a third party arising from your use of the Service</li>
          </ul>
        </section>

        <section id="disputes">
          <h2>20. Dispute Resolution and Binding Arbitration</h2>
          <p>
            <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</strong>
          </p>
          <p>
            <strong>Informal Resolution:</strong> Before initiating any legal proceeding, you agree to attempt to 
            resolve disputes informally by contacting legal@equiforge.com. We will work in good faith 
            to resolve the issue within 60 days. If the dispute is not resolved within 60 days, either party may proceed to binding arbitration.
          </p>
          <p>
            <strong>Binding Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to this EULA, the Service, or the breach, termination, enforcement, interpretation, or validity thereof, shall be determined by binding individual arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules, as modified by this EULA.
          </p>
          <p>
            <strong>Arbitration Terms:</strong>
          </p>
          <ul>
            <li>Arbitration will be conducted in English in San Francisco County, California, or at a location mutually agreed upon</li>
            <li>The arbitration shall be conducted by a single neutral arbitrator</li>
            <li>The arbitrator's decision shall be final, binding, and non-appealable except as permitted by the Federal Arbitration Act</li>
            <li>Each party shall bear its own costs and attorneys' fees, unless the arbitrator determines otherwise</li>
            <li>The arbitrator may award declaratory or injunctive relief only in favor of the individual party seeking relief and only to the extent necessary to provide relief warranted by that party's individual claim</li>
            <li>Judgment on the arbitration award may be entered in any court of competent jurisdiction</li>
          </ul>
          <p>
            <strong>Exceptions:</strong> Either party may pursue claims in small claims court if they qualify. Either party may seek injunctive or other equitable relief in any court of competent jurisdiction to prevent the actual or threatened infringement of intellectual property rights.
          </p>
          <p>
            <strong>Governing Law:</strong> This EULA is governed by and construed in accordance with the laws of the State of California, United States, 
            without regard to its conflict of law principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply.
          </p>
          <p>
            <strong>Opt-Out:</strong> You may opt out of this arbitration clause by sending written notice to legal@equiforge.com within 30 days of first accepting this EULA. Your notice must include your full name, email address associated with your account, and a clear statement that you wish to opt out of the arbitration clause.
          </p>
        </section>

        <section id="class-action-waiver">
          <h2>21. Class Action Waiver</h2>
          <p className="font-semibold">
            YOU AND EQUIFORGE™ AGREE THAT EACH PARTY MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING.
          </p>
          <p>
            Unless both you and EquiForge™ agree otherwise, the arbitrator may not consolidate more than one person's claims and may not otherwise preside over any form of a representative or class proceeding. If a court or arbitrator determines that this class action waiver is void or unenforceable for any reason, or that an arbitration can proceed on a class basis, then the arbitration provision set forth in Section 20 shall be deemed null and void in its entirety and the parties shall be deemed to have NOT agreed to arbitrate disputes.
          </p>
        </section>

        <section id="export">
          <h2>22. Export Compliance</h2>
          <p>
            The application and related technology may be subject to U.S. export control laws and 
            regulations, including the Export Administration Regulations (EAR) and sanctions programs administered by the Office of Foreign Assets Control (OFAC).
          </p>
          <p>You represent and warrant that you are not:</p>
          <ul>
            <li>Located in a country subject to U.S. embargo (including Cuba, Iran, North Korea, Syria, and the Crimea, Donetsk, and Luhansk regions)</li>
            <li>Listed on any U.S. government list of prohibited or restricted parties, including the Specially Designated Nationals (SDN) List</li>
            <li>Restricted from receiving U.S. exports under applicable laws</li>
            <li>Intending to use the Service for any purpose prohibited by U.S. export control laws</li>
          </ul>
        </section>

        <section id="government">
          <h2>23. Government Users</h2>
          <p>
            If you are a U.S. government entity or agency, the application is "commercial computer software" 
            and "commercial computer software documentation" as defined in FAR 12.212 and DFARS 227.7202. 
            Consistent with these regulations, the application is licensed with only those rights as are 
            granted to all other end users pursuant to this EULA.
          </p>
        </section>

        <section id="accessibility">
          <h2>24. Accessibility Statement</h2>
          <p>
            EquiForge™ is committed to ensuring digital accessibility for people with disabilities. We continuously improve the user experience for everyone and strive to conform to WCAG 2.1 Level AA standards. Our efforts include:
          </p>
          <ul>
            <li>Semantic HTML structure for screen reader compatibility</li>
            <li>Keyboard navigation support for all interactive elements</li>
            <li>Sufficient color contrast ratios for text and interactive elements</li>
            <li>Alternative text for images and non-text content</li>
            <li>Responsive design that adapts to various devices and screen sizes</li>
          </ul>
          <p>
            If you encounter any accessibility barriers or have suggestions for improvement, please contact accessibility@equiforge.com.
          </p>
        </section>

        <section id="modification">
          <h2>25. Modification of Terms</h2>
          <p>
            EquiForge™ reserves the right to modify, amend, or update this EULA at any time. We will provide at least 30 days advance notice of material changes through one or more of the following methods:
          </p>
          <ul>
            <li>Email notification to the address associated with your account</li>
            <li>Prominent notice within the application (e.g., banner or modal)</li>
            <li>Requiring re-acceptance of the updated EULA upon next login</li>
          </ul>
          <p>
            Non-material changes (e.g., formatting, clarifications) may be made without prior notice. Your continued use of the Service after any changes take effect constitutes your acceptance of the modified EULA. If you do not agree with the modified terms, you must stop using the Service and delete your account.
          </p>
          <p>
            The "Last Updated" date at the top of this EULA indicates when the most recent changes were made. We encourage you to review this EULA periodically.
          </p>
        </section>

        <section id="entire-agreement">
          <h2>26. Entire Agreement</h2>
          <p>
            This EULA, together with the Terms of Service, Privacy Policy, Cookie Policy, Disclaimers, and any other referenced policies or agreements, 
            constitutes the entire agreement between you and EquiForge™ regarding the Service and 
            supersedes all prior agreements, understandings, representations, and communications, whether written or oral.
          </p>
        </section>

        <section id="severability">
          <h2>27. Severability</h2>
          <p>
            If any provision of this EULA is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the 
            remaining provisions shall continue in full force and effect. The invalid provision shall 
            be modified to the minimum extent necessary to make it valid and enforceable while preserving its original intent.
          </p>
        </section>

        <section id="assignment">
          <h2>28. Assignment</h2>
          <p>
            You may not assign, transfer, delegate, or sublicense your rights or obligations under this EULA without EquiForge™'s 
            prior written consent. EquiForge™ may freely assign this EULA to any successor, affiliate, or acquirer without 
            restriction, including in connection with a merger, acquisition, reorganization, or sale of all or substantially all assets. Any purported assignment in violation of this section is null and void.
          </p>
        </section>

        <section id="force-majeure">
          <h2>29. Force Majeure</h2>
          <p>
            EquiForge™ shall not be liable for any failure or delay in performance due to circumstances 
            beyond its reasonable control, including but not limited to acts of God, war, terrorism, 
            riots, embargoes, natural disasters, pandemics, epidemics, government actions or orders, labor disputes, 
            failures of third-party services, infrastructure outages, cyberattacks, power outages, or 
            internet service disruptions.
          </p>
        </section>

        <section id="contact">
          <h2>30. Contact Information</h2>
          <p>
            For questions, concerns, or notices regarding this EULA, please contact:
          </p>
          <div className="bg-muted/50 rounded-lg p-4 mt-2">
            <p className="mb-1"><strong>General Legal:</strong> legal@equiforge.com</p>
            <p className="mb-1"><strong>Privacy & Data:</strong> privacy@equiforge.com</p>
            <p className="mb-1"><strong>AI Governance:</strong> ai-governance@equiforge.com</p>
            <p className="mb-1"><strong>Accessibility:</strong> accessibility@equiforge.com</p>
            <p className="mb-1"><strong>Billing:</strong> billing@equiforge.com</p>
            <p className="mb-1"><strong>Support:</strong> support@equiforge.com</p>
            <p className="mt-2"><strong>Address:</strong> EquiForge™, Inc. Legal Department, San Francisco, CA, United States</p>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            <strong>Acceptance:</strong> By clicking "I Accept," creating an account, or by installing, accessing, or using 
            the EquiForge™ Homebuyer Optimizer application, you acknowledge that you have read, 
            understood, and agree to be bound by this End User License Agreement in its entirety, including the arbitration clause and class action waiver.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            EULA Version 2.0 • Effective Date: March 9, 2026 • © 2025-2026 EquiForge™, Inc. All rights reserved.
          </p>
        </section>
      </div>
    </ModernPolicyLayout>
  );
};

export default EULA;
